import {
    createSelector,
} from '@reduxjs/toolkit';

import {
    selectAvailableMultibetTypes,
    selectAvailableMultiSingles,
} from './availableBetTypesSelectors';

import {
    selectContingencies,
} from './contingenciesSelectors';

import {
    selectUserHasAnyCastSelection,
} from './userSelectionsSelectors';

import {
    selectSelectionDetails,
    selectCountSuspendedSelections,
} from './selectionDetailsSelectors';

import { selectUserSelections } from './baseBetslipSelectors';

import {
    selectCombinations,
    selectIsOneCombinationOnParlayTab,
} from '../selectors/combinationsSelectors';

import {
    calculateMultipleTotalStake,
} from '~Betslip/utils/stakeCalculators';

import {
    calculatePotentialReturnsMultiplier,
} from '~Betslip/utils/payoutCalculators';

import {
    selectAllFreeBets,
} from './freeBetSelectors';

import { filterCombinationsSelections } from '~Betslip/utils/combinationsUtils';

export const selectParlay = createSelector(
    [
        selectContingencies,
        selectUserHasAnyCastSelection,
        selectAvailableMultibetTypes,
    ],
    (contingencies, hasAnyCastSelection, multibetTypes) => {
        if (contingencies.length || hasAnyCastSelection) {
            return undefined;
        }

        return multibetTypes.find(betType => betType.total === 1);
    }
);

export const selectUserParlay = state => state.betslip.parlayTab.userParlay;
export const selectUserSinglesInParlay = state => state.betslip.parlayTab.userSingles;

export const selectParlayTotalStake = createSelector(
    [
        selectParlay,
        selectUserParlay,
        selectAllFreeBets,
        selectCombinations,
        selectUserSelections,
        selectIsOneCombinationOnParlayTab,
    ],
    (parlay, userParlay, freebets, combinations, userSelections, isOneCombinationOnParlayTab) => {
        // a combination counts as a single bet but is also rendered on the parlay tab
        if (!parlay && combinations.length !== 1) {

            return 0;
        }

        if (isOneCombinationOnParlayTab) {
            const [{ id: combinationId }] = combinations;
            const userCombination = userSelections.find(({ id }) => combinationId === id);
            if (!userCombination) {

                return 0;
            }

            const { stake, free_bet_id } = userCombination;
            const { value: freeBetValue } = freebets[combinationId]?.find(({ id }) => id === free_bet_id) || {};

            return freeBetValue || stake;
        }

        const {
            stake,
            each_way_selected,
            free_bet_id,
        } = userParlay;

        const betslipId = parlay?.id;
        let freebetStake = null;

        if (freebets[betslipId]) {
            const selectedFreebet = freebets[betslipId].find(fb => fb.id === free_bet_id);
            freebetStake = selectedFreebet?.value || null;
        }

        return calculateMultipleTotalStake(stake, each_way_selected, undefined, freebetStake);
    }
);

export const selectSinglesTotalStake = createSelector(
    [
        selectAvailableMultiSingles,
        selectUserSinglesInParlay,
        selectCountSuspendedSelections,
    ],
    (availableMultiSingles, userSingles, suspendedSelectionsCount) => {
        if (!availableMultiSingles) {
            return 0;
        }

        const totalSingles = availableMultiSingles.total - suspendedSelectionsCount;

        const {
            stake,
            each_way_selected,
        } = userSingles;

        return calculateMultipleTotalStake(stake, each_way_selected, totalSingles);
    }
);

export const selectPotentialReturnsMultiplier = createSelector(
    [
        selectSelectionDetails,
        selectCombinations,
    ],
    (selections, combinations = []) => {
        if (!combinations.length) {

            return calculatePotentialReturnsMultiplier(selections);
        }

        const selectionsForReturns = filterCombinationsSelections(combinations, selections);

        return calculatePotentialReturnsMultiplier(selectionsForReturns);
    }
);
